import { useState, useEffect, useRef } from "react";
import { gsap } from "gsap";
import { Menu } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "./ui/sheet";
import { Button } from "./ui/button";

const navLinks = [
  { href: "#home", label: "Home" },
  { href: "#about", label: "About" },
  { href: "#experience", label: "Experience" },
  { href: "#expertise", label: "Expertise" },
  { href: "#projects", label: "Projects" },
  { href: "#consulting", label: "Consulting" },
  { href: "#contact", label: "Contact" },
];

export const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const navRef = useRef(null);
  const logoRef = useRef(null);
  const linksRef = useRef([]);

  useEffect(() => {
    // Initial animation
    const tl = gsap.timeline({ delay: 0.5 });
    
    tl.from(logoRef.current, {
      y: -30,
      opacity: 0,
      duration: 0.6,
      ease: "power3.out"
    })
    .from(linksRef.current, {
      y: -20,
      opacity: 0,
      duration: 0.4,
      stagger: 0.05,
      ease: "power3.out"
    }, "-=0.3");

    // Scroll handler
    const handleScroll = () => {
      const scrolled = window.scrollY > 50;
      setIsScrolled(scrolled);
      
      // Animate nav background
      gsap.to(navRef.current, {
        backgroundColor: scrolled ? "rgba(255,255,255,0.95)" : "transparent",
        backdropFilter: scrolled ? "blur(12px)" : "blur(0px)",
        borderBottomColor: scrolled ? "rgba(0,0,0,0.1)" : "transparent",
        duration: 0.3,
        ease: "power2.out"
      });
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSection = (e, href) => {
    e.preventDefault();
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
      setIsOpen(false);
    }
  };

  return (
    <nav
      ref={navRef}
      data-testid="main-navigation"
      className="fixed top-0 left-0 right-0 z-50 border-b border-transparent"
    >
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <a
            ref={logoRef}
            href="#home"
            onClick={(e) => scrollToSection(e, "#home")}
            data-testid="nav-logo"
            className="font-heading text-3xl md:text-4xl font-bold tracking-tight text-foreground hover:opacity-70 transition-opacity"
          >
            LTVT
          </a>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-6">
            {navLinks.map((link, index) => (
              <a
                key={link.href}
                ref={(el) => linksRef.current[index] = el}
                href={link.href}
                onClick={(e) => scrollToSection(e, link.href)}
                data-testid={`nav-link-${link.label.toLowerCase()}`}
                className="relative text-xs font-medium text-muted-foreground hover:text-foreground transition-colors uppercase tracking-widest group"
              >
                {link.label}
                <span className="absolute -bottom-1 left-0 w-0 h-px bg-foreground group-hover:w-full transition-all duration-300" />
              </a>
            ))}
          </div>

          {/* Mobile Menu */}
          <Sheet open={isOpen} onOpenChange={setIsOpen}>
            <SheetTrigger asChild className="lg:hidden">
              <Button variant="ghost" size="icon" data-testid="mobile-menu-trigger" aria-label="Open menu">
                <Menu className="w-6 h-6" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-full sm:w-80 bg-background border-l border-border">
              <div className="flex flex-col h-full py-12">
                <nav className="flex flex-col gap-6">
                  {navLinks.map((link, index) => (
                    <a
                      key={link.href}
                      href={link.href}
                      onClick={(e) => scrollToSection(e, link.href)}
                      data-testid={`mobile-nav-link-${link.label.toLowerCase()}`}
                      className="text-2xl font-heading font-semibold text-foreground hover:text-muted-foreground transition-colors"
                      style={{ 
                        opacity: isOpen ? 1 : 0,
                        transform: isOpen ? 'translateX(0)' : 'translateX(20px)',
                        transition: `all 0.3s ease ${index * 0.05}s`
                      }}
                    >
                      {link.label}
                    </a>
                  ))}
                </nav>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </nav>
  );
};

export default Navigation;
